import { type FC, useEffect, useMemo } from "react";

import { ContentOverviewReports, GraphReport, Packs, DashboardReport } from "../../features/Reporting";
import NotFoundMessage from "../../components/notFound/NotFoundMessage";
import { Route, Routes } from "react-router";
import { Loader } from "semantic-ui-react";
import { GoogleReport } from "features/Reporting/Google/GoogleReport";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { bindAction } from "interfaces";
import { fetchIntegrationsAction } from "features/Reporting/state/menu/integrationMenuThunk";
import { type ConnectedProps, connect } from "react-redux";
import { useRtn } from "hooks/useRtn";
import { selectMenuItemsData, selectMenuItemsLoading } from "features/Reporting/state/menu/integrationMenuSlice";
import ReportingPeople from "features/Reporting/People/ReportingPeople";
import { isBsi } from "features/Library/Common/utils/performanceUtils";
import { events } from "./constants";
import { catchAll } from "../../utils/routeHelpers";
import { AddOnIds } from "enums";
import { fetchAddOns } from "features/Accounts/state/thunks/addOnsThunk";

export const ReportingContent: FC<PropsFromRedux> = ({
  integrations,
  integrationsLoaded,
  fetchIntegrations,
  accountId,
  addOns,
  fetchAddOns,
}) => {
  const isGoogleIntegrationActive = integrations.Google;
  const isMicrosoftIntegrationEnabled = integrations.MSGraph;

  useEffect(() => {
    fetchAddOns(accountId);
  }, [accountId, fetchAddOns]);

  const hasCreateAccountsAddOn = useMemo(
    () =>
      Array.isArray(addOns?.items) &&
      addOns.items.some((addOn) => addOn.id === AddOnIds.CreateAccounts && addOn.isEnabled),
    [addOns],
  );

  const isBsiAccount = isBsi(accountId);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  useRtn(events, fetchIntegrations);

  return (
    <div className="route-content-container">
      <Routes>
        {!integrationsLoaded && <Route path="*" element={<Loader active />} />}

        {catchAll("overview", <DashboardReport />)}
        {catchAll("packs", <Packs />)}

        {(isMicrosoftIntegrationEnabled || isBsiAccount) && catchAll("graph", <GraphReport />)}

        {catchAll("content", <ContentOverviewReports />)}

        {catchAll("people", <ReportingPeople hasCreateAccountsAddOn={hasCreateAccountsAddOn} />)}
        {catchAll("customers", <ReportingPeople hasCreateAccountsAddOn={hasCreateAccountsAddOn} />)}
        {isGoogleIntegrationActive && catchAll("google", <GoogleReport />)}

        <Route path="*" element={<NotFoundMessage />} />
      </Routes>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  integrations: selectMenuItemsData(state),
  integrationsLoaded: selectMenuItemsLoading(state),
  accountId: state.userProfile.accountId,
  addOns: state.accounts.addOns,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchIntegrations: bindAction(fetchIntegrationsAction, dispatch),
  fetchAddOns: bindAction(fetchAddOns, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(ReportingContent);
export default ConnectedComponent;
